import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { gameBySlug } from "@/lib/games";

const THRESHOLD = 10;

export function BigWinToast({ bet, onOpen }) {
  const g = gameBySlug(bet.game);
  const Icon = g?.icon || Zap;
  return (
    <button onClick={onOpen} className="big-win-toast" data-testid={`big-win-toast-${bet.id}`} style={{ "--accent": g?.accent || "#FFC107" }}>
      <span className="big-win-icon"><Icon size={18} /></span>
      <span className="flex-1 min-w-0 text-left">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-amber-300">Big win · {g?.name || bet.game}</span>
        <span className="block text-sm font-semibold truncate"><span className="text-zinc-300">{bet.username}</span> hit <span className="font-mono-num gold-text font-black">×{Number(bet.multiplier).toFixed(2)}</span></span>
        <span className="block font-mono-num text-xs text-emerald-400">+{formatAmount(bet.profit, bet.currency)} {bet.currency}</span>
      </span>
    </button>
  );
}

export default function BigWinWatcher() {
  const { user } = useAuth();
  const nav = useNavigate();
  const seen = useRef(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/bets/live?limit=30");
        if (!alive) return;
        if (!seen.current) {
          seen.current = new Set(data.map((b) => b.id));
          return;
        }
        const fresh = data.filter((b) => !seen.current.has(b.id));
        data.forEach((b) => seen.current.add(b.id));
        fresh.filter((b) => b.multiplier >= THRESHOLD && b.profit > 0).slice(0, 3).reverse().forEach((b) => {
          const mine = user && b.user_id === user.id;
          toast.custom((id) => <BigWinToast bet={b} onOpen={() => { toast.dismiss(id); nav(`/games/${b.game}`); }} />, {
            duration: mine ? 9000 : 6000,
            position: "bottom-right",
          });
        });
      } catch { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [user, nav]);

  return null;
}
